import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, ArrowRight, Zap, Building2, ShieldCheck,
  Wallet, FileUp, Send, Sparkles,
} from "lucide-react";
import api from "../../lib/api";

const STEP_ICON = {
  profile: Building2,
  mca_intel: ShieldCheck,
  kyc: ShieldCheck,
  funding: Wallet,
  documents: FileUp,
  submit: Send,
};

function ProgressRing({ value = 0, size = 84, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const dash = (pct / 100) * c;
  return (
    <svg width={size} height={size} className="-rotate-90" data-testid="onboarding-progress-ring">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none"
              className="stroke-navy/10" />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${c - dash}`}
              className="stroke-emerald-600 transition-all duration-700 ease-out" />
    </svg>
  );
}

export default function OnboardingHub({ onLoaded }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busySubmit, setBusySubmit] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/startup/onboarding-status");
      setData(res.data);
      onLoaded?.(res.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusySubmit(true);
    try {
      await api.post("/startup/submit-for-review");
      toast.success("Submitted for VenturaZ review.");
      load();
    } catch (e) { toast.error("Submission failed."); }
    finally { setBusySubmit(false); }
  };

  const doneCount = useMemo(
    () => (data?.steps || []).filter((s) => s.done).length,
    [data]
  );
  const total = data?.steps?.length || 6;
  const nba = data?.next_best_action;
  // Only surface the Submit-for-Review button when the founder has never
  // submitted (Draft) OR has edits pending after a prior submission.
  const submittedAt = data?.startup_submitted_at;
  const updatedAt = data?.startup_updated_at;
  const hasPendingChanges = !!(submittedAt && updatedAt && new Date(updatedAt) > new Date(submittedAt));
  const isDraftOrRejected = ["Draft", "Rejected", null, undefined, ""].includes(data?.startup_status);
  const allDone = data && doneCount === total && (isDraftOrRejected || hasPendingChanges);

  if (!data) {
    return (
      <div className="rounded-xl border border-navy/10 bg-white p-6 mb-6 animate-pulse" data-testid="onboarding-hub-loading">
        <div className="h-4 bg-navy/5 rounded w-1/3 mb-4" />
        <div className="h-2 bg-navy/5 rounded w-full" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-navy/10 bg-gradient-to-br from-emerald-50/60 via-white to-white shadow-sm mb-6" data-testid="onboarding-hub">
      <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="relative flex-shrink-0">
          <ProgressRing value={data.overall_progress} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-heading text-2xl text-emerald-700" data-testid="onboarding-progress-pct">
              {data.overall_progress}%
            </div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Journey</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-emerald-600" />
            <div className="overline text-emerald-700">Founder Journey</div>
          </div>
          <div className="font-heading text-xl text-ink" data-testid="onboarding-headline">
            {data.overall_progress === 100 ? "You're all set — review pending"
              : `${doneCount} of ${total} steps complete`}
          </div>
          <div className="text-sm text-ink-muted mt-1">
            {nba
              ? <>Next: <span className="text-ink font-medium">{nba.label}</span> — {nba.hint}</>
              : "All steps complete. VenturaZ team is reviewing your submission."}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
          {allDone ? (
            <button
              onClick={submit}
              disabled={busySubmit}
              className="px-5 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading disabled:opacity-60 transition-colors"
              data-testid="onboarding-submit-review"
            >
              <Send size={14} className="inline mr-2" />
              Submit for Review
            </button>
          ) : nba ? (
            <Link
              to={nba.deep_link}
              className="px-5 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading inline-flex items-center gap-2 transition-colors"
              data-testid="onboarding-next-action"
            >
              <Zap size={14} /> {nba.label} <ArrowRight size={14} />
            </Link>
          ) : null}
          <button
            onClick={() => setExpanded((x) => !x)}
            className="px-4 py-2.5 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04]"
            data-testid="onboarding-toggle-steps"
          >
            {expanded ? "Hide steps" : "Show all steps"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-navy/10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-6 pt-4" data-testid="onboarding-steps-grid">
          {data.steps.map((s) => {
            const Icon = STEP_ICON[s.key] || Circle;
            return (
              <Link
                to={s.deep_link}
                key={s.key}
                className={`rounded-lg border p-4 transition-all hover:shadow-md ${
                  s.done ? "border-emerald-200 bg-emerald-50/40"
                         : "border-navy/10 bg-white hover:border-emerald-300"
                }`}
                data-testid={`onboarding-step-${s.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
                    s.done ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {s.done ? <CheckCircle2 size={18} /> : <Icon size={18} />}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                    {s.eta_min}m
                  </div>
                </div>
                <div className="font-heading text-ink text-sm">{s.label}</div>
                <div className="text-xs text-ink-muted mt-1 line-clamp-2">{s.hint}</div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${s.done ? "bg-emerald-600" : "bg-emerald-400"} transition-all`}
                      style={{ width: `${s.progress}%` }}
                    />
                  </div>
                  <div className="text-[10px] font-mono text-ink-muted w-8 text-right">
                    {s.progress}%
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
